/**
 * Timer Worker - Handles setTimeout operations in a web worker
 * This prevents timer delays when the main thread is busy
 */

import logger from '../logger';

export interface SetTimeoutMessage {
  uid: string;
  type: 'setTimeout';
  delay: number;
}

export interface ClearTimeoutMessage {
  uid: string;
  type: 'clearTimeout';
}

export type TimerWorkerMessage = SetTimeoutMessage | ClearTimeoutMessage;

export interface TimerWorkerResponse {
  uid: string;
}

export default (): void => {
  const activeTimeouts = new Map<string, number>();

  // eslint-disable-next-line no-restricted-globals
  self.onmessage = (message: MessageEvent): void => {
    if (message.data) {
      let data: TimerWorkerMessage | null = null;

      try {
        data = JSON.parse(message.data);
      } catch (error) {
        logger.error('Timer worker: Could not parse message data', error);
      }

      if (data) {
        if (data.type === 'setTimeout') {
          const { uid, delay } = data;

          // Clear any existing timeout for this uid
          const existingTimeout = activeTimeouts.get(uid);
          if (existingTimeout !== undefined) {
            clearTimeout(existingTimeout);
          }

          // Set new timeout and track it
          const timeoutId = setTimeout(() => {
            activeTimeouts.delete(uid);
            // Send the UID back to the main thread to trigger the callback
            postMessage(JSON.stringify({ uid }));
          }, delay || 0) as unknown as number;

          activeTimeouts.set(uid, timeoutId);
        } else if (data.type === 'clearTimeout') {
          const { uid } = data;
          const timeoutId = activeTimeouts.get(uid);

          if (timeoutId !== undefined) {
            clearTimeout(timeoutId);
            activeTimeouts.delete(uid);
          }
        }
      }
    }
  };
};
